import axios from 'axios';
import { WordPressPost } from '../types/wordpress';
import { fetchPosts } from './api';

export interface Tag {
  id: number;
  name: string;
  slug: string;
  count?: number;
  description?: string;
}

export interface TagsResponse {
  tags: Tag[];
  total: number;
}

// API base URL detection
const getApiBaseUrl = (): string => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  if (typeof window !== 'undefined') {
    const { protocol, hostname, port } = window.location;
    
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5000/api`;
    }
    
    return `${protocol}//${hostname}${port ? ':' + port : ''}/api`;
  }
  
  return '/api';
};

const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 15000,
});

class TagService {
  // Cache for ALL tag details - preloaded once
  private tagCache: Map<number, Tag> = new Map();
  private isPreloaded: boolean = false;
  private preloadPromise: Promise<void> | null = null;

  /**
   * Preload ALL tags once to eliminate multiple API calls
   * This method should be called once when app starts
   */
  async preloadAllTags(): Promise<void> {
    if (this.isPreloaded || this.preloadPromise) {
      return this.preloadPromise || Promise.resolve();
    }

    this.preloadPromise = this.doPreload();
    return this.preloadPromise;
  }

  private async doPreload(): Promise<void> {
    try {
      console.log('[TagService] Preloading all tags...');
      const startTime = Date.now();
      
      let allTags: Tag[] = [];
      let page = 1;
      const perPage = 1000; // ↑ INCREASED: Request 1000 tags per call instead of 100
      let hasMore = true;
      
      // Fetch all tags with large batch requests
      while (hasMore) {
        console.log(`[TagService] 📊 Large batch request: page ${page}, requesting ${perPage} tags`);
        
        const response = await apiClient.get(`/tags?per_page=${perPage}&page=${page}&orderby=count&order=desc`);
        const pageTags: Tag[] = response.data.map((tag: any) => ({
          id: tag.id,
          name: tag.name,
          slug: tag.slug,
          count: tag.count,
          description: tag.description
        }));
        
        allTags = [...allTags, ...pageTags];
        
        // Check if we have more pages (if we got fewer than requested, we're done)
        hasMore = pageTags.length === perPage;
        page++;
        
        // With 1000 tags per request, we should rarely need more than 2-3 requests
        // Safety limit reduced since we're getting more per request
        if (page > 10) {
          console.warn('[TagService] Reached page limit (10), stopping pagination');
          break;
        }
        
        console.log(`[TagService] ✅ Large batch ${page - 1} completed: ${pageTags.length} tags (total: ${allTags.length})`);
        
        // Small delay between large requests to be respectful
        if (hasMore) {
          await new Promise(resolve => setTimeout(resolve, 200)); // 200ms delay between large batches
        }
      }

      // Populate cache with all tags
      this.tagCache.clear();
      allTags.forEach(tag => {
        this.tagCache.set(tag.id, tag);
      });

      this.isPreloaded = true;
      const loadTime = Date.now() - startTime;
      console.log(`[TagService] ✅ Preloaded ${allTags.length} tags in ${loadTime}ms (${page - 1} large batches of ${perPage} each)`);
      console.log('[TagService] 📊 Cache statistics:', {
        totalTags: this.tagCache.size,
        requestsMade: page - 1,
        tagsPerRequest: perPage,
        avgTagsPerRequest: Math.round(allTags.length / (page - 1)),
        loadTimeMs: loadTime
      });
      
    } catch (error) {
      console.error('[TagService] ❌ Failed to preload tags:', error);
      // Don't throw error - fallback to individual API calls
    }
  }

  /**
   * Get tag details by IDs - optimized with preloaded cache
   */
  async getTagsByIds(tagIds: number[]): Promise<Tag[]> {
    if (!tagIds || tagIds.length === 0) return [];

    // Ensure tags are preloaded
    if (!this.isPreloaded) {
      await this.preloadAllTags();
    }

    // If preload succeeded, use pure cache lookup
    if (this.isPreloaded) {
      const tags: Tag[] = [];
      const missingIds: number[] = [];

      tagIds.forEach(id => {
        const cached = this.tagCache.get(id);
        if (cached) {
          tags.push(cached);
        } else {
          missingIds.push(id);
        }
      });

      // Log missing tags (shouldn't happen if preload worked)
      if (missingIds.length > 0) {
        console.warn('[TagService] Missing tags from cache:', missingIds);
      }

      return tags;
    }

    // Fallback: original behavior if preload fails
    return this.getTagsByIdsFallback(tagIds);
  }

  /**
   * Fallback method - original behavior for when preload fails
   */
  private async getTagsByIdsFallback(tagIds: number[]): Promise<Tag[]> {
    console.log('[TagService] Using fallback method for tag IDs:', tagIds);
    
    // Check cache first
    const cachedTags: Tag[] = [];
    const uncachedIds: number[] = [];

    tagIds.forEach(id => {
      const cached = this.tagCache.get(id);
      if (cached) {
        cachedTags.push(cached);
      } else {
        uncachedIds.push(id);
      }
    });

    // Fetch uncached tags
    let fetchedTags: Tag[] = [];
    if (uncachedIds.length > 0) {
      try {
        const response = await apiClient.get(`/tags?include=${uncachedIds.join(',')}&per_page=100`);
        fetchedTags = response.data.map((tag: any) => ({
          id: tag.id,
          name: tag.name,
          slug: tag.slug,
          count: tag.count,
          description: tag.description
        }));

        // Cache the fetched tags
        fetchedTags.forEach(tag => {
          this.tagCache.set(tag.id, tag);
        });
      } catch (error) {
        console.error('Error fetching tags:', error);
      }
    }

    return [...cachedTags, ...fetchedTags];
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): { size: number; isPreloaded: boolean; sample: Tag[] } {
    return {
      size: this.tagCache.size,
      isPreloaded: this.isPreloaded,
      sample: Array.from(this.tagCache.values()).slice(0, 5)
    };
  }

  /**
   * Fetch posts by tag ID (not slug) - with full metadata processing
   */
  async getPostsByTag(
    tagId: number, 
    page: number = 1, 
    perPage: number = 12, 
    cacheBuster: boolean = false, 
    languageCategoryId?: string // ✅ NEW: Language filtering
  ): Promise<WordPressPost[]> {
    try {
      console.log(`[TagService] Fetching posts by tag: ${tagId}, page: ${page}, perPage: ${perPage}${cacheBuster ? ' (CACHE BYPASS)' : ''}${languageCategoryId ? ` | Language filter: ${languageCategoryId}` : ''}`);
      
      // Use fetchPosts with tag filtering AND language filtering to get fully processed posts
      const posts = await fetchPosts(page, perPage, undefined, [tagId], cacheBuster, languageCategoryId);
      
      console.log('[TagService] ✅ Fetched posts by tag:', posts.length, 'posts');
      return posts;
    } catch (error) {
      console.error('[TagService] ❌ Error fetching posts by tag:', error);
      throw error;
    }
  }

  /**
   * NEW: Fetch fresh posts by tag with cache bypass
   */
  async getFreshPostsByTag(tagId: number, page: number = 1, perPage: number = 12, languageCategoryId?: string): Promise<WordPressPost[]> {
    console.log(`[TagService] 🆕 Fetching fresh posts by tag: ${tagId} with cache bypass${languageCategoryId ? ` | Language filter: ${languageCategoryId}` : ''}`);
    return this.getPostsByTag(tagId, page, perPage, true, languageCategoryId);
  }

  /**
   * NEW: Get language-specific count for a tag
   */
  async getTagCount(tagSlug: string, languageCategoryId?: string): Promise<number> {
    try {
      console.log(`[TagService] 🔢 Getting language-specific count for tag: ${tagSlug}${languageCategoryId ? ` | Language: ${languageCategoryId}` : ''}`);
      
      const response = await apiClient.get(`/tags/slug/${tagSlug}/count`, {
        params: languageCategoryId ? { languageCategoryId } : {},
        timeout: 10000
      });
      
      if (response.data && typeof response.data.count === 'number') {
        console.log(`[TagService] ✅ Tag count retrieved: ${tagSlug} = ${response.data.count} articles`);
        return response.data.count;
      }
      
      console.log(`[TagService] ⚠️ Invalid count response for tag: ${tagSlug}`);
      return 0;
    } catch (error) {
      console.error(`[TagService] ❌ Error getting tag count for ${tagSlug}:`, error);
      return 0;
    }
  }

  /**
   * Get single tag by slug (OPTIMIZED for TagPage - no massive preloading!)
   */
  async getTagBySlug(slug: string): Promise<Tag | null> {
    try {
      console.log(`[TagService] 🎯 Looking up single tag by slug: "${slug}"`);
      
      // First check if it's in cache (avoid API call)
      for (const tag of this.tagCache.values()) {
        if (tag.slug === slug) {
          console.log(`[TagService] ✅ Found tag in cache: ${tag.name}`);
          return tag;
        }
      }
      
      // Use optimized single tag endpoint (NO MASSIVE PRELOADING!)
      console.log(`[TagService] 🔄 Fetching single tag from API: ${slug}`);
      const response = await apiClient.get(`/tags/slug/${slug}`);
      
      const tag: Tag = {
        id: response.data.id,
        name: response.data.name,
        slug: response.data.slug,
        count: response.data.count,
        description: response.data.description
      };
      
      // Cache the single tag for future use
      this.tagCache.set(tag.id, tag);
      console.log(`[TagService] ✅ Single tag fetched and cached: ${tag.name} (ID: ${tag.id})`);
      
      return tag;
    } catch (error) {
      console.error(`[TagService] ❌ Error fetching tag by slug "${slug}":`, error);
      return null;
    }
  }

  /**
   * Search tags by name - now uses preloaded cache for better performance
   * NOTE: This method triggers massive preloading - avoid for single tag lookups!
   */
  async searchTags(search: string, perPage: number = 20): Promise<Tag[]> {
    console.warn(`[TagService] ⚠️ searchTags called for "${search}" - consider using getTagBySlug for single tag lookups`);
    
    // Ensure tags are preloaded
    if (!this.isPreloaded) {
      await this.preloadAllTags();
    }

    if (this.isPreloaded) {
      // Use cache for search - much faster than API
      const searchLower = search.toLowerCase();
      const results: Tag[] = [];
      
      for (const tag of this.tagCache.values()) {
        if (tag.name.toLowerCase().includes(searchLower) || 
            tag.slug.toLowerCase().includes(searchLower)) {
          results.push(tag);
          if (results.length >= perPage) break;
        }
      }
      
      // Sort by count (popular first)
      return results.sort((a, b) => (b.count || 0) - (a.count || 0));
    }

    // Fallback to API if preload failed
    try {
      const response = await apiClient.get(`/tags?search=${encodeURIComponent(search)}&per_page=${perPage}`);
      return response.data.map((tag: any) => ({
        id: tag.id,
        name: tag.name,
        slug: tag.slug,
        count: tag.count,
        description: tag.description
      }));
    } catch (error) {
      console.error('Error searching tags:', error);
      return [];
    }
  }

  /**
   * Get popular tags - now uses preloaded cache
   */
  async getPopularTags(limit: number = 20): Promise<Tag[]> {
    // Ensure tags are preloaded
    if (!this.isPreloaded) {
      await this.preloadAllTags();
    }

    if (this.isPreloaded) {
      // Use cache - much faster than API
      return Array.from(this.tagCache.values())
        .sort((a, b) => (b.count || 0) - (a.count || 0))
        .slice(0, limit);
    }

    // Fallback to API if preload failed
    try {
      const response = await apiClient.get(`/tags?orderby=count&order=desc&per_page=${limit}`);
      return response.data.map((tag: any) => ({
        id: tag.id,
        name: tag.name,
        slug: tag.slug,
        count: tag.count,
        description: tag.description
      }));
    } catch (error) {
      console.error('Error fetching popular tags:', error);
      return [];
    }
  }

  /**
   * Clear tag cache and reset preload state
   */
  clearCache(): void {
    this.tagCache.clear();
    this.isPreloaded = false;
    this.preloadPromise = null;
  }
}

export const tagService = new TagService();

// Utility function to generate shared cache key for tag queries
const generateTagCacheKey = (intervalMinutes: number = 1): string => {
  // Same logic as in api.ts for consistency
  const now = Date.now();
  const intervalMs = intervalMinutes * 60 * 1000;
  const intervalKey = Math.floor(now / intervalMs);
  
  const date = new Date(intervalKey * intervalMs);
  const dateStr = date.toISOString().slice(0, 16).replace(/[-:T]/g, '');
  
  return `tag_cache_${dateStr}_${intervalMinutes}m`;
};

// Cache bypass strategy for tag queries
const getTagCacheKey = (forceRefresh: boolean = false, conservative: boolean = false): string | undefined => {
  if (!forceRefresh) {
    return undefined;
  }
  
  // Use 1-minute or 5-minute intervals based on conservative flag
  const intervalMinutes = conservative ? 5 : 1;
  const sharedKey = generateTagCacheKey(intervalMinutes);
  
  console.log(`[TagService] 🔑 Generated tag cache key: ${sharedKey} (${intervalMinutes}min interval)`);
  return sharedKey;
};

// ENHANCED: Get posts by tag (always fresh with shared cache keys)
export const getPostsByTag = async (
  tagSlug: string, 
  page = 1, 
  perPage = 10,
  forceNoCache: boolean = false, // Option to force no cache for special cases
  languageCategoryId?: string // ✅ NEW: Language filtering
): Promise<{posts: WordPressPost[], totalPosts: number}> => {
  try {
    // Get tag first
    const tag = await tagService.getTagBySlug(tagSlug);
    if (!tag) {
      console.log(`[TagService] ❌ Tag not found: ${tagSlug}`);
      return { posts: [], totalPosts: 0 };
    }
    
    // 🌟 NEW: fetchPosts now ALWAYS uses shared cache keys for fresh content by default
    const posts = await fetchPosts(page, perPage, undefined, [tag.id], forceNoCache, languageCategoryId);
    
    // Get total count (approximation based on tag count)
    const totalPosts = tag.count || posts.length;
    
    console.log(`[TagService] 🎯 Fetched ${posts.length} posts for tag: ${tagSlug} (always fresh with shared cache)${languageCategoryId ? ` | Language filter: ${languageCategoryId}` : ''}`);
    
    return { posts, totalPosts };
    
  } catch (error) {
    console.error(`[TagService] Error fetching posts for tag ${tagSlug}:`, error);
    return { posts: [], totalPosts: 0 };
  }
};

// Legacy function for backwards compatibility (now just calls main getPostsByTag)
export const getFreshPostsByTag = async (
  tagSlug: string, 
  page = 1, 
  perPage = 10,
  languageCategoryId?: string
): Promise<{posts: WordPressPost[], totalPosts: number}> => {
  console.log(`[TagService] 📢 DEPRECATED: getPostsByTag now always returns fresh content by default`);
  return getPostsByTag(tagSlug, page, perPage, false, languageCategoryId); // No need for special handling
}; 